import { computed, ComputedRef, Ref, ref } from 'vue';
import { useDialog } from '@negar/core';
import {
  PositionHistoryItemWithPrices,
  PositionItemWithPrices,
  TUseOptionPositionDetailMapperReturn,
  calcCashSettlementRequired,
  calcPhysicalSettlementRequired,
} from '@core/option';

import {
  BuyCashSettlementDialog,
  DeleteSettlementDialog,
  SellCashSettlementDialog,
  POSITION_SIDE_LABELS,
  PositionInfoDialog,
  BuyPhysicalSettlementDialog,
  PositionHistoryInfoDialog,
  FailedToDeliverInfo,
  FrozenHistoryTable,
  FreezeUnfreezeDialog,
  ConfirmDeleteFreezeDialog,
  FreezeInfoDialog,
  DeleteStrategyDialog,
  EditStrategyDialog,
  ChainStatusInfoDialog,
} from '@/modules/option';

import { i18n } from '@/plugins/i18n';
import { FrozenHistoryItem } from '@libs/api-reference';

const { t } = i18n.global;

export default function useOptionDialogs() {
  const { add: addDialog } = useDialog();

  const openSellCashSettlementDialog = ({
    isEditMode = false,
    positionSettlementModel,
  }: {
    isEditMode?: boolean;
    positionSettlementModel: Pick<
      PositionItemWithPrices,
      | 'isin'
      | 'instrumentName'
      | 'cashSettlementFee'
      | 'cashSettlementRequestQuantity'
      | 'cashMaximumQuantity'
      | 'shareInProportion'
      | 'underlyingAsseClosingPrice'
      | 'contractSize'
      | 'strikePrice'
      | 'quantity'
    >;
  }) => {
    const title = `${t('option.cashSettlement')} (${
      positionSettlementModel?.instrumentName
    })`;

    addDialog({
      title: title,
      content: computed(() => ({
        component: SellCashSettlementDialog,
        props: {
          isEditMode,
          isin: positionSettlementModel?.isin,
          openPositionQuantity: positionSettlementModel?.quantity,
          positionSettlementModel,
          model: {
            quantity: positionSettlementModel?.cashSettlementRequestQuantity,
            isMaxQuantity: positionSettlementModel?.cashMaximumQuantity,
            shareInProportion: positionSettlementModel?.shareInProportion,
          },
        },
      })),
    });
  };

  const openBuyCashSettlementDialog = ({
    isEditMode = false,
    positionSettlementModel,
  }: {
    isEditMode?: boolean;
    positionSettlementModel: Pick<
      PositionItemWithPrices,
      | 'isin'
      | 'instrumentName'
      | 'cashSettlementRequestQuantity'
      | 'cashMaximumQuantity'
      | 'shareInProportion'
      | 'quantity'
    >;
  }) => {
    const title = `${t('option.cashSettlement')} (${
      positionSettlementModel?.instrumentName
    })`;

    addDialog({
      title: title,
      content: computed(() => ({
        component: BuyCashSettlementDialog,
        props: {
          isEditMode,
          isin: positionSettlementModel?.isin,
          openPositionQuantity: positionSettlementModel?.quantity,
          model: {
            quantity: positionSettlementModel?.cashSettlementRequestQuantity,
            isMaxQuantity: positionSettlementModel?.cashMaximumQuantity,
            shareInProportion: positionSettlementModel?.shareInProportion,
          },
        },
      })),
    });
  };

  const openDeleteSettlementDialog = ({
    position,
    handleDelete,
    referenceElementRef,
    isInCashSettlementDay,
    loading,
  }: {
    position: PositionItemWithPrices;
    handleDelete: (...args) => any;
    referenceElementRef: HTMLElement | undefined;
    isInCashSettlementDay: boolean;
    loading: ComputedRef<boolean>;
  }) => {
    addDialog({
      title: t('option.deleteSettlementConfirmTitle', {
        instrumentName: position.instrumentName,
        settlementType: isInCashSettlementDay
          ? t('option.settlementRequest.cash')
          : t('option.settlementRequest.physical'),
      }),
      referenceElementRef,
      placement: 'top',
      content: computed(() => ({
        component: DeleteSettlementDialog,
        props: {
          position: position,
          settlementType: isInCashSettlementDay
            ? t('option.settlementRequest.cash')
            : t('option.settlementRequest.physical'),
          onDelete: handleDelete,
          loading: loading.value,
        },
      })),
    });
  };

  const openPositionInfoDialog = (
    position: TUseOptionPositionDetailMapperReturn
  ) => {
    const title = t('option.positionInfoTitle', {
      instrumentName: position?.instrumentName,
      positionSide: POSITION_SIDE_LABELS[position.positionSide],
    });

    addDialog({
      bodyClass: '!w-[360px]',
      title: title,
      content: computed(() => ({
        component: PositionInfoDialog,
        props: {
          position,
        },
      })),
    });
  };

  const openPositionHistoryInfoDialog = (
    position: PositionHistoryItemWithPrices
  ) => {
    const title = t('option.positionHistoryInfoTitle', {
      instrumentName: position?.instrumentName,
    });

    addDialog({
      bodyClass: '!w-[360px]',
      title: title,
      content: computed(() => ({
        component: PositionHistoryInfoDialog,
        props: {
          position,
        },
      })),
    });
  };

  const openFailedToDeliverInfoDialog = (quantity: number) => {
    const title = t('option.failedToDeliverQuantityInfo');

    addDialog({
      bodyClass: '!w-[360px]',
      title: title,
      content: computed(() => ({
        component: FailedToDeliverInfo,
        props: {
          quantity,
        },
      })),
    });
  };

  const openBuyPhysicalSettlementDialog = ({
    isEditMode = false,
    positionPhysicalSettlementModel,
  }: {
    positionPhysicalSettlementModel: Pick<
      PositionItemWithPrices,
      | 'isin'
      | 'strikePrice'
      | 'contractSize'
      | 'instrumentName'
      | 'physicalSettlementRequestQuantity'
      | 'physicalMaximumQuantity'
      | 'physicalSettlementFee'
      | 'quantity'
      | 'requestedInCaseOfOtmOrAtm'
    >;
    isEditMode?: boolean;
  }) => {
    const title = `${t('option.physicalSettlement')} (${
      positionPhysicalSettlementModel?.instrumentName
    })`;

    addDialog({
      title: title,
      content: computed(() => ({
        component: BuyPhysicalSettlementDialog,
        props: {
          isEditMode,
          positionPhysicalSettlementModel,
          isin: positionPhysicalSettlementModel?.isin,
          openPositionQuantity: positionPhysicalSettlementModel?.quantity,
          model: {
            quantity:
              positionPhysicalSettlementModel?.physicalSettlementRequestQuantity,
            isMaxQuantity:
              positionPhysicalSettlementModel?.physicalMaximumQuantity,
            physicalSettlementRequestForOutOfMoneyOrEvenContract:
              positionPhysicalSettlementModel?.requestedInCaseOfOtmOrAtm,
          },
        },
      })),
    });
  };

  const openFreezeUnfreezeDialog = () => {
    const isLastFrozenExpanded = ref(true);
    const isFreeze = ref<boolean>(true);

    addDialog({
      title: t('option.requestingFreezeAndUnfreezing'),
      content: computed(() => ({
        component: FreezeUnfreezeDialog,
        props: {
          isFreeze,
          'onUpdate:isFreeze': (val) => (isFreeze.value = val),
        },
      })),
      expandable: true,
      expandableLabel: computed(() =>
        isLastFrozenExpanded.value
          ? `${t('misc.close')} ${t('common.recentRequests')}`
          : t('common.recentRequests')
      ),
      defaultExpanded: isLastFrozenExpanded.value,
      onExpandedUpdate: (newValue) => {
        isLastFrozenExpanded.value = newValue;
      },
      expandedContent: computed(() => ({
        component: FrozenHistoryTable,
        props: {
          isFreeze: computed(() => isFreeze.value),
        },
      })),
    });
  };

  const openDeleteFreezeDialog = ({
    frozenSymbolItem,
    handleDelete,
    loading,
  }: {
    frozenSymbolItem: FrozenHistoryItem;
    handleDelete: (...args) => any;
    loading: ComputedRef<boolean>;
  }) => {
    addDialog({
      title: t('option.cancelFreezeRequest', {
        type: frozenSymbolItem.isFreeze
          ? t('option.freeze')
          : t('option.unFreeze'),
        symbolName: frozenSymbolItem.name,
      }),
      placement: 'top',
      content: computed(() => ({
        component: ConfirmDeleteFreezeDialog,
        props: {
          symbol: frozenSymbolItem,
          onDelete: handleDelete,
          loading: loading.value,
        },
      })),
    });
  };

  const openFreezeInfoDialog = ({
    frozenSymbolItem,
  }: {
    frozenSymbolItem: FrozenHistoryItem;
  }) => {
    addDialog({
      bodyClass: '!w-[360px]',
      title: t('misc.requestDetails'),
      content: computed(() => ({
        component: FreezeInfoDialog,
        props: {
          symbol: frozenSymbolItem,
        },
      })),
    });
  };

  const openChainStatusInfoDialog = () => {
    addDialog({
      bodyClass: '!w-[360px]',
      title: t('option.chainStatus'),
      content: computed(() => ({
        component: ChainStatusInfoDialog,
        props: {},
      })),
    });
  };

  const openDeleteStrategy = ({
    isin,
    loading,
    onDelete,
  }: {
    isin: string;
    loading: boolean;
    onDelete: (isin: string) => void;
  }) => {
    addDialog({
      title: t('option.strategyDeleteDialogTitle'),
      content: computed(() => ({
        component: DeleteStrategyDialog,
        props: {
          isin,
          loading,
          onDelete,
        },
      })),
    });
  };

  const openEditStrategy = ({
    isin,
    quantity,
  }: {
    isin: string;
    quantity: number;
  }) => {
    addDialog({
      title: t('option.strategyEditDialogTitle'),
      content: computed(() => ({
        component: EditStrategyDialog,
        props: {
          isin,
          quantity,
        },
      })),
    });
  };

  return {
    openEditStrategy,
    openDeleteStrategy,
    openPositionInfoDialog,
    openDeleteSettlementDialog,
    openBuyCashSettlementDialog,
    openSellCashSettlementDialog,
    openPositionHistoryInfoDialog,
    openFailedToDeliverInfoDialog,
    openFreezeUnfreezeDialog,
    openDeleteFreezeDialog,
    openFreezeInfoDialog,
    openBuyPhysicalSettlementDialog,
    openChainStatusInfoDialog,
  };
}
